import React from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// TimelineTecnica — timeline visual da fase TÉCNICA do negócio
// ────────────────────────────────────────────────────────────────────────────
// Mesmo padrão visual da Timeline comercial, adaptado pras etapas técnicas.
// Tipos de evento reconhecidos:
//   • "Etapa Técnica: <nome>" → ponto + label acima da linha
//   • "DOS010 Vinculado"      → ícone abaixo da linha (chapado)
//   • "Anotação"              → ícone abaixo da linha
//   • "Anexo"                 → ícone abaixo da linha
//   • "Call"                  → ícone abaixo da linha
// Medição de dias (Xd) só aparece entre eventos de Etapa Técnica.
// ═══════════════════════════════════════════════════════════════════════════════

const COR = {
  texto:  '#1a1a2e',
  muted:  '#888',
  borda:  '#e8eaed',
  linha:  '#cbd5e1',
  hoje:   '#f59e0b',
  branco: '#fff',
};

// Cores das etapas técnicas
const ETAPA_CORES = {
  'Planejamento':       '#0891b2',
  'Execução':           '#d97706',
  'Qualidade':          '#7c3aed',
  'Validação Cliente':  '#6264a7',
  'Acompanhamento':     '#1D9E75',
  'Encerrado':          '#059669',
};

// Abreviação das etapas técnicas
const ETAPA_SIGLA = {
  'Planejamento':       'PLAN',
  'Execução':           'EXEC',
  'Qualidade':          'QUAL',
  'Validação Cliente':  'VAL',
  'Acompanhamento':     'ACOMP',
  'Encerrado':          'ENC',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function parseEtapa(tipoEvento) {
  // "Etapa Técnica: Planejamento" → "Planejamento"
  if (!tipoEvento || !tipoEvento.startsWith('Etapa Técnica: ')) return null;
  return tipoEvento.substring(15);
}

function isEtapa(ev)     { return !!parseEtapa(ev.tipo_evento); }
function isAnexo(ev)     { return ev.tipo_evento === 'Anexo'; }
function isCall(ev)      { return ev.tipo_evento === 'Call'; }
function isDOS010(ev)    { return ev.tipo_evento === 'DOS010 Vinculado'; }
function isAnotacao(ev)  { return ev.tipo_evento === 'Anotação'; }

function formatarDataCurta(v) {
  if (!v) return '';
  const d = new Date(v);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function diffDias(d1, d2) {
  const a = new Date(d1); a.setHours(0,0,0,0);
  const b = new Date(d2); b.setHours(0,0,0,0);
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

// ─── Ícones SVG chapados (sem moldura) ──────────────────────────────────────
function IconeAnexo({ cor = '#1D9E75', size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={cor}
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
    </svg>
  );
}

function IconeCall({ cor = '#6264a7', size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={cor}
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15.05 5A5 5 0 0119 8.95M15.05 1A9 9 0 0123 8.94m-1 7.98v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
    </svg>
  );
}

function IconeDOS010({ cor = '#003399', size = 24 }) {
  // Documento com linhas (DOS010 = documento de quantificação)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={cor}
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="8" y1="13" x2="16" y2="13"/>
      <line x1="8" y1="17" x2="13" y2="17"/>
    </svg>
  );
}

function IconeAnotacao({ cor = '#d97706', size = 24 }) {
  // Lápis/edição
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={cor}
         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9"/>
      <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4z"/>
    </svg>
  );
}

function IconeHoje({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={COR.hoje}
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" fill={COR.hoje}/>
      <line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
function TimelineTecnica({ eventos, onEventoClick, negocio }) {
  if (!eventos || eventos.length === 0) {
    return (
      <div style={{
        padding: '20px', textAlign: 'center',
        fontSize: '12px', color: COR.muted,
        backgroundColor: '#fafafa', borderRadius: '6px',
        border: `1px dashed ${COR.borda}`,
      }}>
        Nenhum evento para exibir na timeline.
      </div>
    );
  }

  // Ordena todos por data
  const ordenados = [...eventos].sort(
    (a, b) => new Date(a.data_evento) - new Date(b.data_evento)
  );

  // Separa etapas e outros (mantendo ordem original)
  const etapas  = ordenados.filter(isEtapa);
  const outros  = ordenados.filter(e => !isEtapa(e));

  // ── Monta a fila horizontal: etapas definem a linha base e as "colunas"
  // Os outros eventos se encaixam cronologicamente entre as etapas.
  //
  // Regra: cada etapa é uma coluna com seu point acima.
  // Eventos não-etapa são distribuídos nas colunas com base em posição
  // cronológica aproximada (vão abaixo da linha base).
  // ─────────────────────────────────────────────────────────────────────

  const hoje = new Date();
  let hojeInserido = false;
  const colunasEtapa = [];
  for (const e of etapas) {
    const dataEv = new Date(e.data_evento);
    if (!hojeInserido && dataEv >= hoje) {
      colunasEtapa.push({ tipoCol: 'hoje', data: hoje });
      hojeInserido = true;
    }
    colunasEtapa.push({ tipoCol: 'etapa', evento: e, data: dataEv });
  }
  if (!hojeInserido) {
    colunasEtapa.push({ tipoCol: 'hoje', data: hoje });
  }

  // Distribui os "outros" nas colunas:
  // Pra cada evento, encontra a coluna mais PRÓXIMA cronologicamente,
  // comparando apenas DATA (sem horário) pra evitar bugs com hora atual.
  // Se houver coluna com a mesma data, ela é a escolhida (incluindo HOJE).
  const eventosPorColuna = new Map();

  // Helper: zera hora pra comparar só a data
  const soData = (d) => {
    const dt = new Date(d);
    dt.setHours(0, 0, 0, 0);
    return dt.getTime();
  };

  for (const ev of outros) {
    const dataEvSemHora = soData(ev.data_evento);
    let melhorIdx = 0;
    let menorDistancia = Infinity;

    for (let i = 0; i < colunasEtapa.length; i++) {
      const dataColSemHora = soData(colunasEtapa[i].data);
      const distancia = Math.abs(dataEvSemHora - dataColSemHora);
      // Preferência: distância menor; em empate, fica com a primeira encontrada
      // (mas como percorremos em ordem cronológica, a empate vai pra mais cedo
      // se mesmo dia houver múltiplas colunas — em geral só HOJE coincide)
      if (distancia < menorDistancia) {
        menorDistancia = distancia;
        melhorIdx = i;
      }
    }

    if (!eventosPorColuna.has(melhorIdx)) eventosPorColuna.set(melhorIdx, []);
    eventosPorColuna.get(melhorIdx).push(ev);
  }

  // Se não tem nenhuma etapa, cria uma coluna "virtual" pra pendurar os outros
  if (colunasEtapa.length === 1 && colunasEtapa[0].tipoCol === 'hoje' && outros.length > 0) {
    // Empilha todos os outros na única coluna
    eventosPorColuna.set(0, outros);
  }

  const ALTURA_ACIMA = 60;   // espaço reservado acima da linha pras etapas
  const ALTURA_ABAIXO = 90;  // espaço abaixo da linha pros anexos/calls
  const LINHA_Y = ALTURA_ACIMA; // Y da linha central

  return (
    <div style={{
      padding: '16px 12px 16px',
      overflowX: 'auto', overflowY: 'hidden',
      backgroundColor: '#fff',
      borderRadius: '6px',
      border: `1px solid ${COR.borda}`,
    }}>
      <div style={{
        position: 'relative',
        minWidth: '100%', width: 'fit-content',
        minHeight: `${ALTURA_ACIMA + ALTURA_ABAIXO}px`,
        display: 'flex', alignItems: 'stretch',
      }}>
        {colunasEtapa.map((col, idx) => {
          const proximaCol = colunasEtapa[idx + 1];
          const gap = proximaCol ? diffDias(col.data, proximaCol.data) : null;
          const outrosDaColuna = eventosPorColuna.get(idx) || [];

          // ─── Tag empresa de faturamento (só pra coluna da etapa Fechado) ───
          // Pega direto do negócio (sempre atualizado). Fallback: parsear
          // descrição (eventos legacy criados antes desta funcionalidade).
          let tagEmpresa = null;
          if (col.tipoCol === 'etapa') {
            const nomeEt = parseEtapa(col.evento.tipo_evento);
            if (nomeEt === 'Fechado') {
              if (negocio?.empresa_faturamento_nome) {
                tagEmpresa = String(negocio.empresa_faturamento_nome).trim();
              } else if (col.evento.descricao) {
                const m = String(col.evento.descricao).match(/Faturamento:\s*(.+)/);
                if (m && m[1].trim()) tagEmpresa = m[1].trim();
              }
            }
          }
          const corColuna = col.tipoCol === 'etapa'
            ? (ETAPA_CORES[parseEtapa(col.evento.tipo_evento)] || '#6b7280')
            : COR.hoje;

          return (
            <React.Fragment key={idx}>
              {/* ═══ Coluna do evento de etapa (ou marcador Hoje) ═══ */}
              <div style={{
                position: 'relative',
                flexShrink: 0,
                minWidth: '160px',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center',
              }}>
                {/* ─── Parte de cima: nó de etapa ─── */}
                <div style={{
                  height: `${ALTURA_ACIMA}px`,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'flex-end',
                  paddingBottom: '6px',
                }}>
                  {col.tipoCol === 'hoje' ? (
                    (() => {
                      // ── Sol mostra dias acumulados desde a 1ª etapa (Oportunidade) ──
                      const primeira = etapas[0];
                      const msPorDia = 1000 * 60 * 60 * 24;
                      const diasAcum = primeira
                        ? Math.max(0, Math.round(
                            (col.data.getTime() - new Date(primeira.data_evento).getTime()) / msPorDia
                          ))
                        : 0;
                      return (
                        <>
                          <IconeHoje />
                          <div style={{
                            fontSize: '10px', fontWeight: '700', color: COR.hoje,
                            textTransform: 'uppercase', letterSpacing: '0.5px',
                            marginTop: '2px',
                          }}>Hoje</div>
                          {primeira && (
                            <div style={{
                              fontSize: '10px',
                              fontWeight: '600',
                              color: COR.hoje,
                              marginTop: '3px',
                            }}>
                              {diasAcum}d
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    (() => {
                      const nomeEtapa = parseEtapa(col.evento.tipo_evento);
                      const corE = ETAPA_CORES[nomeEtapa] || '#6b7280';

                      // ── Dias acumulados desde a 1ª etapa (Oportunidade) ──
                      // Pra Oportunidade (1ª) sempre é 0d.
                      const primeira = etapas[0];
                      const msPorDia = 1000 * 60 * 60 * 24;
                      const diasAcum = primeira
                        ? Math.max(0, Math.round(
                            (col.data.getTime() - new Date(primeira.data_evento).getTime()) / msPorDia
                          ))
                        : 0;

                      return (
                        <>
                          <div title={col.evento.tipo_evento} style={{
                            backgroundColor: corE,
                            color: COR.branco,
                            padding: '6px 14px',
                            borderRadius: '4px',
                            fontSize: '11px', fontWeight: '700',
                            textTransform: 'uppercase', letterSpacing: '0.4px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          }}>
                            {nomeEtapa}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            color: corE,
                            marginTop: '3px',
                          }}>
                            {diasAcum}d
                          </div>
                          <div style={{
                            fontSize: '9px', color: COR.muted,
                            marginTop: '2px',
                          }}>
                            {formatarDataCurta(col.data)}
                          </div>
                        </>
                      );
                    })()
                  )}
                </div>

                {/* ─── Linha base ─── */}
                <div style={{
                  position: 'absolute',
                  top: `${LINHA_Y}px`, left: 0, right: 0,
                  height: '2px', backgroundColor: COR.linha,
                }} />

                {/* ─── Dot na linha (indica posição da etapa) ─── */}
                <div style={{
                  position: 'absolute',
                  top: `${LINHA_Y - 3}px`,
                  width: '8px', height: '8px', borderRadius: '50%',
                  backgroundColor: col.tipoCol === 'hoje' ? COR.hoje
                    : (ETAPA_CORES[parseEtapa(col.evento?.tipo_evento)] || '#6b7280'),
                  border: `2px solid ${COR.branco}`,
                  boxSizing: 'content-box',
                }} />

                {/* ─── Tag empresa de faturamento (só pra coluna Fechado) ─── */}
                {/* Posicionada ABSOLUTAMENTE abaixo da linha base, pra ficar bem
                    visível sem colidir com nada. Aparece centrada na coluna. */}
                {tagEmpresa && tagEmpresa.length > 0 && (
                  <div title={`Empresa de faturamento: ${tagEmpresa}`} style={{
                    position: 'absolute',
                    top: `${LINHA_Y + 14}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: corColuna,
                    backgroundColor: corColuna + '15',
                    border: `1px solid ${corColuna}55`,
                    borderRadius: '12px',
                    maxWidth: '160px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: '1.3',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    zIndex: 1,
                  }}>
                    🏢 {tagEmpresa}
                  </div>
                )}

                {/* ─── Parte de baixo: eventos "outros" desta coluna ─── */}
                {/* Tudo centralizado verticalmente. Quando há 2+ eventos no
                    mesmo dia, uma linha vertical cinza fina conecta o
                    primeiro ao próximo, indicando que estão agrupados. */}
                <div style={{
                  // Quando a coluna tem tag empresa (Fechado), aumenta a margem
                  // pra dar espaço suficiente abaixo da linha + tag flutuante.
                  marginTop: tagEmpresa && tagEmpresa.length > 0 ? '46px' : '12px',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: '0',
                  maxWidth: '160px',
                  width: '100%',
                }}>
                  {outrosDaColuna.map((ev, idxEv) => {
                    let corIc, IconeComp;
                    if (isCall(ev))         { corIc = '#6264a7'; IconeComp = IconeCall; }
                    else if (isDOS010(ev))  { corIc = '#003399'; IconeComp = IconeDOS010; }
                    else if (isAnotacao(ev)){ corIc = '#d97706'; IconeComp = IconeAnotacao; }
                    else                    { corIc = '#1D9E75'; IconeComp = IconeAnexo; }
                    const ehUltimo = idxEv === outrosDaColuna.length - 1;
                    const totalEventos = outrosDaColuna.length;

                    return (
                      <React.Fragment key={ev.id}>
                        <div
                          onClick={() => onEventoClick && onEventoClick(ev)}
                          title={`${ev.tipo_evento}: ${ev.descricao}`}
                          style={{
                            cursor: onEventoClick ? 'pointer' : 'default',
                            display: 'flex', flexDirection: 'column', alignItems: 'center',
                            gap: '2px',
                            padding: '4px',
                          }}
                        >
                          <IconeComp cor={corIc} size={15} />
                          <div style={{
                            fontSize: '9px', color: COR.muted,
                            marginTop: '2px',
                          }}>
                            {formatarDataCurta(ev.data_evento)}
                          </div>
                          <div style={{
                            fontSize: '9px', color: COR.texto,
                            maxWidth: '140px', overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }}>
                            {ev.descricao}
                          </div>
                        </div>

                        {/* Linha vertical cinza conectora — só quando há 2+
                            eventos no mesmo dia, e não desenha após o último */}
                        {totalEventos > 1 && !ehUltimo && (
                          <div style={{
                            width: '1px',
                            height: '14px',
                            backgroundColor: COR.borda,
                          }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              {/* ═══ Conector entre esta coluna e a próxima (só entre etapas) ═══ */}
              {gap !== null && (
                <div style={{
                  position: 'relative',
                  flex: '0 0 auto',
                  minWidth: gap === 0 ? '30px' : Math.min(60 + gap * 2, 120) + 'px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  height: `${ALTURA_ACIMA}px`,
                  alignSelf: 'flex-start',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: `${LINHA_Y}px`, left: 0, right: 0,
                    height: '2px', backgroundColor: COR.linha,
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: `${LINHA_Y - 20}px`,
                    fontSize: '10px', color: COR.muted, fontWeight: '600',
                    padding: '1px 6px',
                    backgroundColor: COR.branco,
                    border: `1px solid ${COR.borda}`,
                    borderRadius: '10px',
                  }}>
                    {gap === 0 ? '0d' : `${gap}d`}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

export default TimelineTecnica;
